import type {
  ExecuteContractActionPayload,
  ExecuteContractActionResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodExecuteContractActionResponseSchema } from './validations';

/**
 * Executes an action on a contract (status transition).
 * Available actions: confirm, start, complete, cancel, delete.
 *
 * @param contractId - The UUID of the contract to perform the action on.
 * @param payload - The action payload containing the action type and optional notes.
 * @returns The updated contract with available actions.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/execute_contract_action_contracts__contract_id__action_post
 */
export const executeContractAction = async (
  contractId: UUID,
  payload: ExecuteContractActionPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.CONTRACT.ACTION(contractId), {
      json: payload,
    })
    .json<ExecuteContractActionResponse>();

  // Validate the response with Zod
  const validatedResponse =
    zodExecuteContractActionResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      error: {
        errorMessage: validatedResponse?.error?.message,
        errors: validatedResponse?.error?.issues,
        response,
      },
      message: 'Execute contract action API response has validation errors',
    });
  }

  return response;
};
